import React, {createContext, useContext, ReactNode} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useFormik, FormikProvider, FormikErrors} from 'formik';
import * as actions from '../../CustomerDetails/actions/CustomerGreenInvoiceActions';
import moment from 'moment';
import {usePriceQuoteDetailsContext} from './PriceQuoteDetailsContext';
import {useTextSuggestionsContext} from "../../_common/contexts/TextSuggestionsContext";
import {documentTypes as T} from "../../CustomerDetails/constants/documentTypes";

interface VatType {
  value: number;
  label?: string;
}

interface RowCommonDoc {
  description: string;
  quantity: string | number;
  price: string | number;
  vatType: VatType | null;
}

interface FormValues {
  clientId: string;
  description: string;
  rowsCommonDocs: RowCommonDoc[];
  amount: string;
  type: any;
  remarks: string;
  footer: string;
  linkedDocumentIds: number[];
  createdDate: string;
}

interface ContextType {
  formik: ReturnType<typeof useFormik<FormValues>>;
  handleCreate: () => Promise<void>;
  handlePreview: () => Promise<void>;
}

interface RootState {
  auth: {
    greenInvoiceApiKey?: string;
    greenInvoiceSecret?: string;
  };
}

const defaultContext: ContextType = {
  formik: {} as ReturnType<typeof useFormik<FormValues>>,
  handleCreate: async () => {},
  handlePreview: async () => {}
}

const CreatePriceQuoteGIContext = createContext<ContextType>(defaultContext);

export function useCreatePriceQuoteGIContext() {
  return useContext(CreatePriceQuoteGIContext);
}

export function CreatePriceQuoteGIProvider({children}: {children: ReactNode}) {
  const dispatch = useDispatch<any>();

  const {greenInvoiceApiKey, greenInvoiceSecret} = useSelector(
    (state: RootState) => ({
      greenInvoiceApiKey: state.auth.greenInvoiceApiKey,
      greenInvoiceSecret: state.auth.greenInvoiceSecret
    }),
    shallowEqual
  );

  const {greenInvoiceClientID, getFileFromGI, setLoadingFile, formik: detailsFormik} = usePriceQuoteDetailsContext();

  const greenInvoiceIsEnabled = !!greenInvoiceApiKey && !!greenInvoiceSecret && !!greenInvoiceClientID;

  const validate = (values: FormValues): FormikErrors<FormValues> => {
    const errors: any = {};

    if (values.rowsCommonDocs.length === 0) {
      errors.rows = 'Required at least one row';
    }

    values.rowsCommonDocs.forEach((item) => {
      if (!item.description) errors['rowsCommonDocs-description'] = 'Required line item';
      if (!item.vatType) errors['rowsCommonDocs-vatType'] = 'Required line item';

      if (
        item.vatType &&
        typeof item.vatType === 'object' &&
        !(item.vatType.value === 1 || item.vatType.value === 0)
      ) {
        errors['rowsCommonDocs-vatType2'] = 'Required line item';
      }

      if (!item.quantity) errors['rowsCommonDocs-quantity'] = 'Required line item';
      if (!item.price) errors['rowsCommonDocs-price'] = 'Required line item';
    });

    return errors;
  };

  const {textSuggestions} = useTextSuggestionsContext();
  const {remarks} = textSuggestions;
  const getSuggestions = (array: typeof remarks) =>
    Array.isArray(array) ? array.filter(item => item.documentTypeID === T.PRICE_QUOTE) : [];

  const formik = useFormik<FormValues>({
    initialValues: {
      clientId: '',
      description: '',
      rowsCommonDocs: detailsFormik.values.lines,
      amount: '0.00',
      type: null,
      remarks: getSuggestions(remarks).length > 0
        ? getSuggestions(remarks)[0].value
        : '',
      footer: '',
      linkedDocumentIds: [],
      createdDate: moment().format('YYYY-MM-DD')
    },
    validate,
    validateOnMount: true,
    onSubmit: () => {},
    enableReinitialize: true
  });

  const preparedData = () => {
    const {values} = formik;

    const mappedRows = Array.isArray(values.rowsCommonDocs) ? values.rowsCommonDocs.map(item => {
      const {vatType, ...rest} = item;
      return {vatType: typeof vatType === 'object' ? vatType?.value : vatType, ...rest}
    }) : [];

    return {
      clientId: greenInvoiceClientID,
      discount: {
        amount: values.amount,
        type: values.type
      },
      description: values.description,
      rows: mappedRows,
      remarks: values.remarks,
      footer: values.footer,
      date: moment(values.createdDate).format('YYYY-MM-DD')
    };
  };

  const handleCreate = async () => {
    if (!greenInvoiceIsEnabled) return;

    const priceQuote = await dispatch(
      actions.createPriceQuote(
        preparedData(),
        greenInvoiceApiKey!,
        greenInvoiceSecret!
      )
    );

    setLoadingFile(true);

    setTimeout(() => {
      if (priceQuote) {
        const fileName = priceQuote?.number ?? '';
        const url = priceQuote?.url?.origin ?? '';
        getFileFromGI(fileName, url);
      }
      setLoadingFile(false);
    }, 10000);
  };

  const handlePreview = async () => {
    if (!greenInvoiceIsEnabled) return;

    const file = await dispatch(
      actions.previewPriceQuote(
        preparedData(),
        greenInvoiceApiKey!,
        greenInvoiceSecret!
      )
    );

    if (file) {
      const link = `data:application/pdf;base64,${file}`;
      const popup = window.open();

      if (popup) {
        popup.document.write(`<iframe width="100%" height="100%" src="${link}"></iframe>`);
      }
    }
  };

  return (
    <CreatePriceQuoteGIContext.Provider
      value={{
        formik,
        handleCreate,
        handlePreview
      }}
    >
      <FormikProvider value={formik}>{children}</FormikProvider>
    </CreatePriceQuoteGIContext.Provider>
  );
}